import { usePostRequestFormErrorApi, usePutRequestFormErrorApi } from 'api/request_form_error'
import { Flex } from 'components/shared/flex'
import { DateField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, CloseButton, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { RequestFormError, RequestFormErrorForm } from 'entities/request_form_error'
import { useFetchInstitutionsApi } from 'api/institution'
import { InstitutionSearchForm } from 'entities/institution'
import { RequestFormErrorViewModel } from 'view_models/request_form_error_view_model'

export type FormViewProps = {
    form: Form<RequestFormErrorForm>
    apiError: ApiError
}

type NewRequestFormErrorFormProps = {
    onComplete?: () => void
}

export const NewRequestFormErrorForm = (props: NewRequestFormErrorFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostRequestFormErrorApi()
    const form = useForm<RequestFormErrorForm>({ itemList: [] })
    const [show, setShow] = useState<boolean>(false)

    const searchForm = useForm<InstitutionSearchForm>({ perPage: 100000 })
    const institutionApi = useFetchInstitutionsApi(searchForm)
    useEffect(() => {
        institutionApi.execute()
    }, [])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    const disabled = () => {
        if (!form.object.requestFormCode) {
            return true
        } else if (form.object.userName && form.object.birthday) {
            return false
        } else if (form.object.karteNumber) {
            return false
        } else {
            return true
        }
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>検査依頼書不備報告の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <InputField form={form} attr="requestFormCode" label="依頼書コード" placeholder="バーコード読み取り" apiError={api.apiError} required />
                        <Flex>
                            <InputField form={form} attr="karteNumber" apiError={api.apiError} label="カルテ番号" />
                            <InputField form={form} attr="userName" apiError={api.apiError} label="ユーザー名" />
                            <DateField form={form} attr="birthday" apiError={api.apiError} label="生年月日" showYearDropdown />
                        </Flex>
                        <div style={{ marginBottom: 10, marginTop: 10 }}>不備があった箇所をすべて入力してください</div>
                        {form.object.itemList &&
                            form.object.itemList.map((_, index) => {
                                const items = ['カルテ番号', 'フリガナ', '名前', '生年月日', '提出医', '採取日', '購入製品']
                                if (items.length <= index) {
                                    return
                                }
                                return (
                                    <Flex key={'item-list-' + index} alignItems="center" style={{ marginBottom: 10 }}>
                                        <SelectField
                                            style={{ marginBottom: 0 }}
                                            form={form}
                                            attr={['itemList', index]}
                                            selectItems={items
                                                .filter((i) => !form.object.itemList!.includes(i) || form.object.itemList![index] === i)
                                                .map((i) => {
                                                    return { label: i, value: i }
                                                })}
                                            includeBlank
                                        />
                                        <CloseButton
                                            onClick={() => {
                                                form.update((f) => {
                                                    f.itemList!.splice(index, 1)
                                                })
                                            }}
                                        />
                                    </Flex>
                                )
                            })}
                        <Button
                            variant="secondary"
                            style={{ width: '100%' }}
                            disabled={form.object.itemList?.some((i) => !i)}
                            onClick={() => {
                                form.object.itemList ? form.update((f) => f.itemList!.push(undefined)) : form.updateObject('itemList', [])
                            }}
                        >
                            項目追加
                        </Button>
                        <div style={{ marginTop: 10 }}>
                            <InputField form={form} attr="note" as="textarea" label="備考" />
                        </div>
                        <div>※ 登録後Slackメッセージが送信されます</div>
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate} disabled={disabled()}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditRequestFormErrorFormProps = {
    requestFormError: RequestFormError
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditRequestFormErrorForm = (props: EditRequestFormErrorFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutRequestFormErrorApi()
    const form = useForm<RequestFormErrorForm>(props.requestFormError)

    const vm = () => {
        return new RequestFormErrorViewModel(props.requestFormError)
    }

    useEffectSkipFirst(() => {
        form.set({ ...props.requestFormError, itemDict: vm().items() })
    }, [props.requestFormError])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>正しい値を入力してください</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ marginBottom: 10 }}>ユーザー名: {form.object.userName}</div>
                {form.object.id && (
                    <div>
                        <Table>
                            <thead>
                                <tr>
                                    <td style={{ width: 150 }}>項目</td>
                                    <td>正しい値</td>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(new RequestFormErrorViewModel(form.object).items()).map((key, index) => {
                                    return (
                                        <tr key={'item-dict-' + index}>
                                            <td style={{ verticalAlign: 'middle' }}>{key}</td>
                                            <td>
                                                <InputField form={form} attr={['itemDict', key]} style={{ marginBottom: 0 }} />
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
